import { useEffect, useState } from "react";
import type {
  Project,
  Repository,
  PublishedVersion,
  Values,
} from "@proof/core";
import { useWorkspaceTransport } from "./WorkspaceContext";
import { Proof } from "./Proof";
import { Bulk } from "./Bulk";
import { Control } from "./components";
export function PublishedWorkspaceView({
  sourceParameters,
  onSourceParametersChange,
  project,
  repository,
  mode,
  values,
  onValues,
  partId,
  onPart,
  notify,
  onPreviewSelection,
}: {
  sourceParameters?: import("@proof/core").SourceParameters;
  onSourceParametersChange?: (
    values: import("@proof/core").SourceParameters,
  ) => void;
  project: Project;
  repository: Repository;
  mode: "proof" | "bulk";
  values: Values;
  onValues: (values: Values) => void;
  partId: string;
  onPart: (id: string) => void;
  notify: (message: string) => void;
  onPreviewSelection?: (
    selection: { projectId: string; versionId: string } | undefined,
  ) => void;
}) {
  const transport = useWorkspaceTransport();
  const [versions, setVersions] = useState<PublishedVersion[]>(),
    [selected, setSelected] = useState(""),
    [error, setError] = useState("");
  useEffect(() => {
    let live = true;
    void repository
      .versions(project.tenantId, project.id)
      .then((rows) => {
        if (!live) return;
        setVersions(rows);
        setSelected(rows.at(-1)?.id || "");
      })
      .catch((cause) => {
        if (live) setError(cause.message);
      });
    return () => {
      live = false;
    };
  }, [repository, project.id]);
  useEffect(() => {
    const version = versions?.find((row) => row.id === selected);
    onPreviewSelection?.(
      version ? { projectId: project.id, versionId: version.id } : undefined,
    );
    return () => onPreviewSelection?.(undefined);
  }, [project.id, selected, versions, onPreviewSelection]);
  if (error) return <p role="alert">{error}</p>;
  if (!versions) return <p role="status">Loading published versions…</p>;
  const version = versions.find((row) => row.id === selected),
    definition = version?.definition || project;
  return (
    <>
      <section className="panel padded">
        <Control label="Document version">
          <select
            value={selected}
            onChange={(event) => {
              setSelected(event.target.value);
              onValues({});
              onSourceParametersChange?.({});
              onPart("");
            }}
          >
            {transport.can("tenant.documents.manage") && (
              <option value="">Draft preview</option>
            )}
            {versions.map((row) => (
              <option key={row.id} value={row.id}>
                Published v{row.version}
              </option>
            ))}
          </select>
        </Control>
        <p>
          {version
            ? "Generation and saved proofs use this immutable published version. Changing versions resets local form inputs."
            : "Draft preview updates locally. Save changes to refresh host values; publish and select a version before generating or saving proofs."}
        </p>
        {!versions.length && (
          <p>
            No published versions.{" "}
            {transport.can("tenant.templates.publish")
              ? "Use Publish to create the first immutable version."
              : "Ask a workspace publisher to publish this document."}
          </p>
        )}
      </section>
      {!version && !transport.can("tenant.documents.manage") ? (
        <p>No published version is available for proofing.</p>
      ) : mode === "proof" ? (
        <Proof
          sourceParameters={sourceParameters}
          onSourceParametersChange={onSourceParametersChange}
          key={selected || "draft"}
          project={definition}
          repository={repository}
          values={values}
          onValues={onValues}
          partId={partId}
          onPart={onPart}
          versionId={version?.id}
          notify={notify}
        />
      ) : version ? (
        <Bulk
          key={version.id}
          project={definition}
          versionId={version.id}
          notify={notify}
        />
      ) : (
        <p role="status">
          Select a published version to prepare bulk generation.
        </p>
      )}
    </>
  );
}
